/**
 * Shared, framework-agnostic helpers for order status across all 4 order types
 * (Dine-In / MyEats / Catering / Junior Eats).
 *
 * Goals (built for mid/low-literacy restaurant staff):
 *  - One canonical, color + icon coded status for every raw status the API returns.
 *  - A single "what is the next step" answer so the UI can show ONE obvious button
 *    instead of asking the user to read a dropdown of statuses.
 *
 * This module contains NO JSX so it stays easy to unit test and reuse.
 */

/** Normalize any raw status into a comparable key (no spaces / hyphens / underscores, upper case). */
export function normalizeStatusKey(raw) {
    return String(raw ?? '')
        .trim()
        .toUpperCase()
        .replace(/[\s_-]+/g, '');
}

/**
 * Canonical status definitions. `label` is the plain, friendly word shown to staff.
 * Colors are chosen for strong, glanceable contrast on a busy kitchen tablet.
 */
const STATUS_META = {
    NEW: { key: 'NEW', label: 'New', color: '#1d4ed8', bg: '#dbeafe', icon: 'new' },
    ACCEPTED: { key: 'ACCEPTED', label: 'Accepted', color: '#6d28d9', bg: '#ede9fe', icon: 'accepted' },
    PREPARING: { key: 'PREPARING', label: 'Cooking', color: '#b45309', bg: '#fef3c7', icon: 'cooking' },
    READY: { key: 'READY', label: 'Ready', color: '#0e7490', bg: '#cffafe', icon: 'ready' },
    OFD: { key: 'OFD', label: 'Out for delivery', color: '#c2410c', bg: '#ffedd5', icon: 'delivery' },
    PICKEDUP: { key: 'PICKEDUP', label: 'Picked up', color: '#0f766e', bg: '#ccfbf1', icon: 'pickup' },
    SERVED: { key: 'SERVED', label: 'Served', color: '#15803d', bg: '#dcfce7', icon: 'served' },
    DELIVERED: { key: 'DELIVERED', label: 'Delivered', color: '#15803d', bg: '#dcfce7', icon: 'done' },
    COMPLETED: { key: 'COMPLETED', label: 'Completed', color: '#15803d', bg: '#dcfce7', icon: 'done' },
    CANCELLED: { key: 'CANCELLED', label: 'Cancelled', color: '#b91c1c', bg: '#fee2e2', icon: 'cancelled' },
    UNDELIVERED: { key: 'UNDELIVERED', label: 'Not delivered', color: '#9a3412', bg: '#fde4d3', icon: 'cancelled' },
    /** Catering quote workflow (customer quote requests) */
    REQUESTED: { key: 'REQUESTED', label: 'Requested', color: '#1d4ed8', bg: '#dbeafe', icon: 'new' },
    UNDERREVIEW: { key: 'UNDERREVIEW', label: 'Under review', color: '#b45309', bg: '#fef3c7', icon: 'accepted' },
    CONFIRMED: { key: 'CONFIRMED', label: 'Confirmed', color: '#15803d', bg: '#dcfce7', icon: 'done' },
    REJECTED: { key: 'REJECTED', label: 'Rejected', color: '#b91c1c', bg: '#fee2e2', icon: 'cancelled' },
    UNKNOWN: { key: 'UNKNOWN', label: 'N/A', color: '#475569', bg: '#e2e8f0', icon: 'unknown' },
};

/** Alias every raw/normalized status the backend may send to a canonical key. */
const STATUS_ALIASES = {
    NEW: 'NEW',
    PENDING: 'NEW',
    ACC: 'ACCEPTED',
    ACCEPTED: 'ACCEPTED',
    PREP: 'PREPARING',
    PREPARING: 'PREPARING',
    INPROGRESS: 'PREPARING',
    RDY: 'READY',
    READY: 'READY',
    OFD: 'OFD',
    OUTFORDELIVERY: 'OFD',
    INTRANSIT: 'OFD',
    INTRANST: 'OFD',
    PU: 'PICKEDUP',
    PICKEDUP: 'PICKEDUP',
    SRV: 'SERVED',
    SERVED: 'SERVED',
    SERVING: 'SERVED',
    DEL: 'DELIVERED',
    DELIVERED: 'DELIVERED',
    CMP: 'COMPLETED',
    COMPLETE: 'COMPLETED',
    COMPLETED: 'COMPLETED',
    PAID: 'COMPLETED',
    CAN: 'CANCELLED',
    CANCELLED: 'CANCELLED',
    UND: 'UNDELIVERED',
    UNDELIVERED: 'UNDELIVERED',
    REQUESTED: 'REQUESTED',
    UNDERREVIEW: 'UNDERREVIEW',
    CONFIRMED: 'CONFIRMED',
    REJECTED: 'REJECTED',
};

/** Resolve a raw status to its canonical key (e.g. "OFD"/"in-transit" -> "OFD"). */
export function resolveStatusKey(raw) {
    const norm = normalizeStatusKey(raw);
    return STATUS_ALIASES[norm] || (STATUS_META[norm] ? norm : 'UNKNOWN');
}

/** Full display meta (label, colors, icon name) for any raw status. */
export function getStatusMeta(raw) {
    return STATUS_META[resolveStatusKey(raw)] || STATUS_META.UNKNOWN;
}

/** Statuses that have no further action (the order is finished). */
const TERMINAL_KEYS = new Set(['DELIVERED', 'COMPLETED', 'CANCELLED', 'PICKEDUP', 'UNDELIVERED']);

export function isTerminalStatus(raw) {
    return TERMINAL_KEYS.has(resolveStatusKey(raw));
}

/**
 * Forward progression per order type. Values are the DISPLAY status strings that
 * `handleStatusChange` already understands (kept identical to the existing
 * *_STATUS_UPDATE_OPTIONS in TodayOrder so the backend mapping is unchanged).
 */
const ORDER_FLOWS = {
    offline: ['NEW', 'PREPARING', 'READY', 'SERVED'],
    catering: ['NEW', 'PREPARING', 'READY', 'OUT-FOR-DELIVERY'],
    jreats: ['NEW', 'OUT-FOR-DELIVERY', 'DELIVERED'],
    myeats: ['NEW', 'PREPARING', 'READY', 'OUT-FOR-DELIVERY', 'DELIVERED'],
    myeatsPickup: ['NEW', 'PREPARING', 'READY', 'PICKED-UP'],
};

/** Store (grocery) order flows — pickup vs delivery. */
const STORE_ORDER_FLOWS = {
    pickup: ['NEW', 'COMPLETED'],
    delivery: ['NEW', 'OUT-FOR-DELIVERY', 'DELIVERED'],
};

/** Plain verbs for the single next-step button, keyed by the status being moved TO. */
const NEXT_STEP_ACTION_LABELS = {
    ACCEPTED: 'Accept',
    PREPARING: 'Start cooking',
    READY: 'Mark ready',
    OFD: 'Send out',
    PICKEDUP: 'Mark picked up',
    SERVED: 'Mark served',
    DELIVERED: 'Mark delivered',
    NEW: 'Mark new',
    COMPLETED: 'Complete',
};

/** Map any order-type filter value to one of the known flow keys. */
export function resolveFlowKey(orderTypeFilter) {
    const t = String(orderTypeFilter ?? '').toLowerCase();
    if (t === 'offline' || t === 'dine-in' || t === 'dinein') return 'offline';
    if (t === 'catering') return 'catering';
    if (t === 'jreats') return 'jreats';
    return 'myeats';
}

/** MyEats delivery vs pickup kitchen flow. */
export function resolveMyEatsFlowKey(order) {
    if (!order) return 'myeats';
    const orderType = String(
        order.orderType ?? order.Order_Type ?? order.order_type ?? 'DELIVERY'
    )
        .trim()
        .toUpperCase();
    return orderType === 'PICKUP' ? 'myeatsPickup' : 'myeats';
}

/**
 * Given an order type + current status, return the single next step or null if
 * the order is already at the end of its flow / in a terminal state.
 *
 * @param {string} orderTypeFilter
 * @param {string} currentStatus
 * @param {object} [order] — when provided for MyEats, picks delivery vs pickup flow
 * @returns {{ nextStatus: string, actionLabel: string, nextKey: string } | null}
 */
export function getNextOrderStep(orderTypeFilter, currentStatus, order = null) {
    if (isTerminalStatus(currentStatus)) return null;
    let flowKey = resolveFlowKey(orderTypeFilter);
    if (flowKey === 'myeats') {
        flowKey = resolveMyEatsFlowKey(order);
    }
    const flow = ORDER_FLOWS[flowKey];
    if (!flow) return null;

    const currentKey = resolveStatusKey(currentStatus);
    const idx = flow.findIndex((s) => resolveStatusKey(s) === currentKey);

    // Unknown/legacy status that maps into this flow's start: advance from the beginning.
    const nextStatus = idx === -1 ? flow[1] : flow[idx + 1];
    if (!nextStatus) return null;

    const nextKey = resolveStatusKey(nextStatus);
    return {
        nextStatus,
        nextKey,
        actionLabel: NEXT_STEP_ACTION_LABELS[nextKey] || `Mark ${getStatusMeta(nextStatus).label}`,
    };
}

/** Pickup vs delivery flow for store (POS / My Eats / Restaurant) grocery orders. */
export function resolveStoreFlowKey(orderTypeFilter, order) {
    const channel = String(orderTypeFilter ?? 'pos').toLowerCase();
    const orderType = String(order?.orderType ?? order?.Order_Type ?? 'DELIVERY')
        .trim()
        .toUpperCase();
    if (channel === 'pos' && orderType === 'PICKUP') return 'pickup';
    if (channel === 'pos' && orderType !== 'DELIVERY') return 'pickup';
    if (orderType === 'PICKUP') return 'pickup';
    return 'delivery';
}

/**
 * Next actionable step for a store grocery order.
 * @returns {{ nextStatus: string, nextKey: string, actionLabel: string, requiresDeliveryAgent: boolean } | null}
 */
export function getNextStoreOrderStep(orderTypeFilter, order) {
    const raw =
        order?.orderStatusCode ??
        order?.Order_Status_Code ??
        order?.order_status_code ??
        order?.orderStatus ??
        order?.OrderStatus ??
        'NEW';
    if (isTerminalStatus(raw)) return null;

    const flowKey = resolveStoreFlowKey(orderTypeFilter, order);
    const flow = STORE_ORDER_FLOWS[flowKey];
    if (!flow) return null;

    const currentKey = resolveStatusKey(raw);
    const idx = flow.findIndex((s) => resolveStatusKey(s) === currentKey);
    const nextStatus = idx === -1 ? flow[1] : flow[idx + 1];
    if (!nextStatus) return null;

    const nextKey = resolveStatusKey(nextStatus);
    return {
        nextStatus,
        nextKey,
        actionLabel: NEXT_STEP_ACTION_LABELS[nextKey] || `Mark ${getStatusMeta(nextStatus).label}`,
        requiresDeliveryAgent: nextKey === 'OFD' && flowKey === 'delivery',
    };
}

/** OrderStatusPill channel for store orders list/detail. */
export function storeOrderStatusChannel(orderTypeFilter, order) {
    return resolveStoreFlowKey(orderTypeFilter, order) === 'delivery' ? 'myeats' : 'offline';
}
